import React, {FC, useEffect, useMemo} from 'react';
import classnames from 'classnames';
import {IconButton, makeStyles} from '@material-ui/core';
import {Icon, Wrapper, Text, Tooltip, Row} from 'components-lib';
import {IInputFile} from 'models';

const isImageFile = (file?: IInputFile | null) => !!file?.type?.startsWith('image/');

interface IAttachmentAreaProps {
  error?: string;
  loading: boolean;
  deleteCallback: () => void;
  file: IInputFile;
  justify?: 'center' | 'flex-start' | 'flex-end' | 'space-between';
  align?: 'center' | 'flex-start' | 'flex-end';
}

export const AttachmentArea: FC<IAttachmentAreaProps> = ({
  error,
  loading,
  deleteCallback,
  file,
  justify = 'space-between',
  align = 'center',
}) => {
  const classes = useStyles({justify, align});
  const previewUrl = useMemo(() => (isImageFile(file) ? window.URL.createObjectURL(file) : ''), [file]);

  useEffect(() => {
    if (!previewUrl) {
      return undefined;
    }

    return () => {
      window.URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const fileName = !error && (
    <Row justify="center">
      <Tooltip title={file?.name}>
        <Text.Heading classes={{root: classes.fileName}} as="h4" color="textPrimary">
          {file?.name}
        </Text.Heading>
      </Tooltip>
    </Row>
  );

  const deleteButton = (
    <IconButton type="button" disabled={loading} onClick={deleteCallback}>
      <Icon.DeleteIcon color={loading ? 'disabled' : 'error'} />
    </IconButton>
  );

  return (
    <Wrapper className={classnames(classes.attachmentArea, {[classes.imageAttachment]: !!previewUrl})}>
      {previewUrl ? (
        <img className={classes.preview} src={previewUrl} alt={file?.name || 'Uploaded file'} />
      ) : (
        <Wrapper className={classes.fileIconContainer}>
          <Icon.FileIcon />
        </Wrapper>
      )}
      {previewUrl ? (
        <Row justify="center" align="center">
          {fileName}
          {error && <Text.Error withBottomMargin={false}>{error}</Text.Error>}
          {deleteButton}
        </Row>
      ) : (
        <>
          {fileName}
          {error && <Text.Error withBottomMargin={false}>{error}</Text.Error>}
          {deleteButton}
        </>
      )}
    </Wrapper>
  );
};

type TAttachmentAreaStyle = Pick<IAttachmentAreaProps, 'justify' | 'align'>;

const useStyles = makeStyles((theme) => ({
  attachmentArea: ({justify, align}: TAttachmentAreaStyle) => {
    return {
      width: '100%',
      display: 'flex',
      justifyContent: justify,
      alignItems: align,
    };
  },
  imageAttachment: {
    flexDirection: 'column',
    justifyContent: 'center',
  },
  preview: {
    display: 'block',
    maxWidth: '100%',
    maxHeight: 420,
    objectFit: 'contain',
    marginBottom: theme.spacing(2),
  },
  fileIconContainer: {
    paddingRight: theme.spacing(1) + theme.spacing(2),
  },
  fileName: {
    maxWidth: 300,
    fontWeight: 700,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
}));
